import React, { useEffect, useRef, useState } from "react";

import MDBox from "components/MDBox";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

// Wrap DefaultDoughnutChart to intercept slice clicks
import { Doughnut } from "react-chartjs-2";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDTypography from "components/MDTypography";
import configs from "examples/Charts/DoughnutCharts/DefaultDoughnutChart/configs";

const COLORS = ["success", "info", "warning", "primary", "secondary", "error"];

function DonutChart({ chart, title, handleSubCat, ...props }) {
  const [data, setdata] = useState({ scoreArray: [], labels: [], rawScores: [] });

  useEffect(() => {
    const scoreArray = [];
    const labels = [];
    for (let i = 0; i < chart?.length; i++) {
      scoreArray.push(chart[i]?.score ?? chart[i]?.Score ?? 0);
      labels.push(chart[i]?.name || chart[i]?.Name || `Item ${i + 1}`);
    }
    // When all scores are 0 Chart.js renders nothing — use equal weights so slices stay visible
    const allZero = scoreArray.length > 0 && scoreArray.every((s) => s === 0);
    setdata({ scoreArray: allZero ? scoreArray.map(() => 1) : scoreArray, labels, rawScores: scoreArray });
  }, [chart]);

  const chartConfig = {
    labels: data.labels,
    datasets: {
      label: title,
      backgroundColors: COLORS.slice(0, data.labels.length),
      data: data.scoreArray,
    },
  };

  const THEME_COLORS = ["#4CAF50", "#49a3f1", "#fb8c00", "#e91e63", "#7b809a", "#F44335"];
  const GREY = "#d0d0d0";
  // Slices with real score 0 are grey; others get their theme colour
  const sliceColors = (data.rawScores || data.scoreArray).map(
    (s, i) => (s === 0 ? GREY : THEME_COLORS[i % THEME_COLORS.length])
  );

  const { data: chartJsData, options } = configs(
    chartConfig.labels,
    chartConfig.datasets,
    chartConfig.cutout
  );
  // Override background colours so zero-score slices are grey
  if (chartJsData.datasets?.[0]) {
    chartJsData.datasets[0].backgroundColor = sliceColors;
    chartJsData.datasets[0].hoverBackgroundColor = sliceColors;
  }

  const handleClick = (_event, elements) => {
    if (elements.length > 0 && handleSubCat && chart) {
      const idx = elements[0].index;
      if (chart[idx]) handleSubCat(chart[idx]);
    }
  };

  const optionsWithCursor = {
    ...options,
    plugins: {
      ...options.plugins,
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const real = data.rawScores[ctx.dataIndex] ?? ctx.parsed;
            return ` ${ctx.label}: ${real}`;
          },
        },
      },
      legend: {
        display: true,
        position: "bottom",
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: "circle",
          font: { size: 12 },
          generateLabels: (chart) => {
            return chart.data.labels.map((label, i) => ({
              text: label,
              fillStyle: sliceColors[i] ?? THEME_COLORS[i % THEME_COLORS.length],
              strokeStyle: sliceColors[i] ?? THEME_COLORS[i % THEME_COLORS.length],
              hidden: false,
              index: i,
            }));
          },
        },
      },
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? "pointer" : "default";
    },
  };

  return (
    <Card>
      <MDBox py={2} pr={2} pl={1}>
        <MDBox display="flex" px={1} pt={1}>
          <MDBox
            width="4rem"
            height="4rem"
            bgColor="primary"
            variant="gradient"
            coloredShadow="primary"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            color="white"
            mt={-5}
            mr={2}
          >
            <Icon fontSize="medium">donut_small</Icon>
          </MDBox>
          <MDBox mt={-2}>
            <MDTypography variant="h6">{title || "Sub Categories"}</MDTypography>
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {props.report?.name}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <MDBox height="19.125rem">
          <Doughnut data={chartJsData} options={optionsWithCursor} onClick={handleClick} />
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default DonutChart;
